import { useState } from "react";
import { Music, Sparkles, Loader2, Sun, Sunset, Moon, Sunrise, Play, Zap } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const API = BACKEND_URL;

const GENRES = ["", "Hip Hop", "R&B", "Southern Soul", "Jazz", "Latin", "Rock", "Afrobeats", "Reggae", "Gospel", "Electronic", "Pop", "Country"];
const MOODS = ["", "Uplifting", "Chill", "Party", "Reflective", "Romantic", "Aggressive", "Melancholic", "Focus"];
const TIMES = [
  { value: "", label: "Any time" },
  { value: "morning", label: "Morning", icon: Sunrise },
  { value: "afternoon", label: "Afternoon", icon: Sun },
  { value: "evening", label: "Evening", icon: Sunset },
  { value: "latenight", label: "Late Night", icon: Moon },
];

export const PlaylistIntelligence = ({ authToken }) => {
  const [filters, setFilters] = useState({
    genre: "",
    mood: "",
    bpm_min: "",
    bpm_max: "",
    time_of_day: "",
    limit: 12,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recommended, setRecommended] = useState([]);
  const [airplay, setAirplay] = useState([]);

  const run = async () => {
    setLoading(true); setError(""); setRecommended([]); setAirplay([]);
    try {
      const body = {
        genre: filters.genre,
        mood: filters.mood,
        bpm_min: filters.bpm_min ? parseInt(filters.bpm_min) : null,
        bpm_max: filters.bpm_max ? parseInt(filters.bpm_max) : null,
        time_of_day: filters.time_of_day,
        limit: filters.limit,
      };
      const r = await fetch(`${API}/api/ai/broadcast/playlist/recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Recommendation failed");
      setRecommended(d.recommended_tracks || []);
      setAirplay(d.airplay_tracks || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div data-testid="playlist-intelligence">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
        <Music className="w-5 h-5 text-cyan-400" /> Playlist Intelligence
      </h3>

      {/* Filters */}
      <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5 mb-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Genre</label>
            <select value={filters.genre} onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
              data-testid="playlist-genre">
              {GENRES.map((g) => <option key={g} value={g}>{g || "Any"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Mood</label>
            <select value={filters.mood} onChange={(e) => setFilters({ ...filters, mood: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
              data-testid="playlist-mood">
              {MOODS.map((m) => <option key={m} value={m}>{m || "Any"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">BPM min</label>
            <input type="number" min="40" max="220" value={filters.bpm_min}
              onChange={(e) => setFilters({ ...filters, bpm_min: e.target.value })}
              placeholder="any"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">BPM max</label>
            <input type="number" min="40" max="220" value={filters.bpm_max}
              onChange={(e) => setFilters({ ...filters, bpm_max: e.target.value })}
              placeholder="any"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
          </div>
        </div>

        <div>
          <label className="text-white/50 text-xs block mb-1.5">Time of day</label>
          <div className="flex flex-wrap gap-2">
            {TIMES.map((t) => {
              const Icon = t.icon;
              const active = filters.time_of_day === t.value;
              return (
                <button key={t.value} onClick={() => setFilters({ ...filters, time_of_day: t.value })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    active
                      ? "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"
                      : "bg-white/5 text-white/50 border-white/5 hover:border-white/15"
                  }`}
                  data-testid={`playlist-time-${t.value || 'any'}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />} {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <button onClick={run} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50"
          data-testid="playlist-run">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Building playlist...</> : <><Sparkles className="w-4 h-4" /> Recommend Songs</>}
        </button>
      </div>

      {/* Results */}
      {airplay.length > 0 && (
        <div className="mb-6" data-testid="playlist-airplay">
          <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-emerald-400" /> From your airplay history ({airplay.length})
          </h4>
          <div className="space-y-1.5">
            {airplay.map((t, i) => (
              <div key={i} className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg px-4 py-2.5 flex items-center gap-3">
                <Play className="w-3.5 h-3.5 text-emerald-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{t.artist} — {t.title}</p>
                  {(t.genre || t.bpm) && (
                    <p className="text-white/40 text-xs">
                      {t.genre && <span>{t.genre}</span>}{t.genre && t.bpm ? " · " : ""}{t.bpm && <span>{t.bpm} BPM</span>}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {recommended.length > 0 && (
        <div data-testid="playlist-recommended">
          <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" /> AI recommendations ({recommended.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {recommended.map((t, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/5 rounded-lg px-4 py-2.5 flex items-center gap-3 hover:border-white/15 transition">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Music className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{t.artist} — {t.title}</p>
                  <p className="text-white/40 text-xs truncate">
                    {t.genre && <span>{t.genre}</span>}
                    {t.genre && t.mood ? " · " : ""}
                    {t.mood && <span>{t.mood}</span>}
                    {(t.genre || t.mood) && t.bpm ? " · " : ""}
                    {t.bpm && <span>{t.bpm} BPM</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && recommended.length === 0 && airplay.length === 0 && (
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-12 text-center">
          <Music className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/40 text-sm">Choose filters and click <span className="text-cyan-400">Recommend Songs</span> to build a playlist.</p>
        </div>
      )}
    </div>
  );
};

export default PlaylistIntelligence;
